// ── Work Schedule Section Editor (Calendar) ────────────────────────────────────
// NOT a Gantt chart — uses calendar/timeline view with milestones
// Uses react-day-picker for date selection and milestone display

import { useState, useMemo } from 'react';
import { Plus, Trash2, Flag, Calendar } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { uid } from '../../lib/uid';


const MILESTONE_COLORS = [
  '#c9a84c', '#ef4444', '#3b82f6', '#22c55e', '#f97316', '#8b5cf6', '#ec4899',
];

export default function WorkScheduleSection({ data, onChange }) {
  const schedule = data || { start_date: '', end_date: '', milestones: [] };
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const update = (key, value) => {
    onChange({ ...schedule, [key]: value });
  };

  // ── Milestone management ───────────────────────────────────────────────
  const addMilestone = () => {
    const milestones = [...(schedule.milestones || [])];
    const colorIdx = milestones.length % MILESTONE_COLORS.length;
    milestones.push({
      id: uid(),
      name: '',
      date: '',
      color: MILESTONE_COLORS[colorIdx],
      description: '',
    });
    update('milestones', milestones);
  };

  const removeMilestone = (idx) => {
    const milestones = [...(schedule.milestones || [])];
    milestones.splice(idx, 1);
    update('milestones', milestones);
    if (selectedMilestone === idx) setSelectedMilestone(null);
  };

  const updateMilestone = (idx, field, value) => {
    const milestones = [...(schedule.milestones || [])];
    milestones[idx] = { ...milestones[idx], [field]: value };
    update('milestones', milestones);
  };

  // ── DayPicker modifiers ────────────────────────────────────────────────
  const milestoneDates = useMemo(() => {
    return (schedule.milestones || [])
      .filter(m => m.date)
      .map(m => new Date(m.date + 'T00:00:00'));
  }, [schedule.milestones]);

  const milestoneDateMap = useMemo(() => {
    const map = {};
    (schedule.milestones || []).forEach(m => {
      if (m.date) map[m.date] = m;
    });
    return map;
  }, [schedule.milestones]);

  // Custom day render to show milestone dots
  const DayContent = (props) => {
    const dateStr = props.date.toISOString().slice(0, 10);
    const milestone = milestoneDateMap[dateStr];
    return (
      <div className="relative flex items-center justify-center">
        <span>{props.date.getDate()}</span>
        {milestone && (
          <span
            className="absolute -bottom-0.5 w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: milestone.color || '#c9a84c' }}
          />
        )}
      </div>
    );
  };

  // Calculate month range for DayPicker
  const calMonth = schedule.start_date
    ? new Date(schedule.start_date + 'T00:00:00')
    : new Date();

  return (
    <div className="space-y-4">
      {/* Date range */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={schedule.start_date || ''}
            onChange={(e) => update('start_date', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
            End Date
          </label>
          <input
            type="date"
            value={schedule.end_date || ''}
            onChange={(e) => update('end_date', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
        </div>
      </div>

      {/* Calendar + Milestones layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Calendar */}
        <div className="bg-white border border-[#e5e4df] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[#c9a84c]" />
            <span className="text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider">
              Calendar
            </span>
          </div>
          <div className="flex justify-center">
            <DayPicker
              month={calMonth}
              modifiers={{
                milestone: milestoneDates,
              }}
              modifiersStyles={{
                milestone: {
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(201, 168, 76, 0.1)',
                  borderRadius: '50%',
                },
              }}
              components={{
                DayContent,
              }}
              styles={{
                caption: { color: '#1a1a2e' },
                head_cell: { color: '#6b7280', fontSize: '12px' },
              }}
              className="text-sm"
            />
          </div>
        </div>

        {/* Milestones list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-[#c9a84c]" />
              <span className="text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider">
                Milestones
              </span>
            </div>
            <button
              type="button"
              onClick={addMilestone}
              className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>

          {(schedule.milestones || []).length === 0 ? (
            <div className="text-center py-8 bg-white border border-[#e5e4df] rounded-lg">
              <Flag className="w-8 h-8 mx-auto text-[#c9a84c]/30 mb-2" />
              <p className="text-xs text-[#9ca3af]">No milestones yet</p>
              <p className="text-xs text-[#9ca3af]">Add milestones to mark key dates</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(schedule.milestones || []).map((m, idx) => (
                <div key={m.id} className="p-3 bg-white border border-[#e5e4df] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: m.color || '#c9a84c' }}
                      />
                      <span className="text-xs font-semibold text-[#1a1a2e]">
                        {m.name || `Milestone #${idx + 1}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMilestone(idx)}
                      className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs text-[#6b7280] mb-0.5">Name</label>
                      <input
                        type="text"
                        value={m.name || ''}
                        onChange={(e) => updateMilestone(idx, 'name', e.target.value)}
                        placeholder="Milestone name"
                        className="w-full px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6b7280] mb-0.5">Date</label>
                      <input
                        type="date"
                        value={m.date || ''}
                        onChange={(e) => updateMilestone(idx, 'date', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#6b7280] mb-0.5">Description</label>
                    <input
                      type="text"
                      value={m.description || ''}
                      onChange={(e) => updateMilestone(idx, 'description', e.target.value)}
                      placeholder="Brief description..."
                      className="w-full px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                    />
                  </div>
                  {/* Color picker */}
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs text-[#6b7280] mr-1">Color:</span>
                    {MILESTONE_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateMilestone(idx, 'color', color)}
                        className="w-4 h-4 rounded-full border-2 transition-transform"
                        style={{
                          backgroundColor: color,
                          borderColor: m.color === color ? '#1a1a2e' : 'transparent',
                          transform: m.color === color ? 'scale(1.2)' : 'scale(1)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
